/** Keep lightweight page slots, but only retain canvases near the viewport. */
export const selectNearbyPdfPages = (pages, root, maximumPages = 8) => {
  if (!pages.length) return [];
  const viewport = root.getBoundingClientRect();
  if (viewport.height <= 0) return pages.slice(0, maximumPages);
  const top = viewport.top - viewport.height;
  const bottom = viewport.bottom + viewport.height;
  // Page slots remain ordered even when their canvas has been released.
  let low = 0;
  let high = pages.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (pages[middle].getBoundingClientRect().bottom < top) low = middle + 1;
    else high = middle;
  }
  const candidates = [];
  const midpoint = (viewport.top + viewport.bottom) / 2;
  for (let index = low; index < pages.length; index += 1) {
    const rect = pages[index].getBoundingClientRect();
    if (rect.top > bottom) break;
    candidates.push({ page: pages[index], distance: Math.abs((rect.top + rect.bottom) / 2 - midpoint) });
  }
  return candidates.sort((a, b) => a.distance - b.distance)
    .slice(0, maximumPages).map(({ page }) => page);
};

export const releasePdfPageCanvas = (wrapper) => {
  wrapper.querySelectorAll('canvas').forEach((canvas) => {
    // Removing a canvas alone does not immediately release its bitmap.
    canvas.width = 0;
    canvas.height = 0;
  });
  wrapper.replaceChildren();
};

/** One render at a time; a page leaving the window cancels its in-flight render. */
export const createPdfPageWindow = ({ root, pages, renderPage, onError, onRelease = () => {} }) => {
  let disposed = false;
  let frame = null;
  let active = null;
  let pending = Promise.resolve();
  let wanted = new Set();
  const retained = new Set();
  const failed = new Set();

  const pump = async () => {
    while (!disposed) {
      const wrapper = Array.from(wanted).find((page) => !retained.has(page) && !failed.has(page));
      if (!wrapper) break;
      const controller = new AbortController();
      active = { wrapper, controller };
      try {
        await renderPage(wrapper, controller.signal);
        if (!disposed && !controller.signal.aborted && wanted.has(wrapper)) retained.add(wrapper);
        else {
          onRelease(wrapper);
          releasePdfPageCanvas(wrapper);
        }
      } catch (error) {
        onRelease(wrapper);
        releasePdfPageCanvas(wrapper);
        if (!disposed && !controller.signal.aborted) {
          failed.add(wrapper);
          onError(error);
        }
      } finally {
        active = null;
      }
    }
  };

  const refresh = () => {
    frame = null;
    if (disposed) return;
    wanted = new Set(selectNearbyPdfPages(pages, root));
    retained.forEach((wrapper) => {
      if (!wanted.has(wrapper)) {
        onRelease(wrapper);
        releasePdfPageCanvas(wrapper);
        retained.delete(wrapper);
      }
    });
    if (active) {
      if (!wanted.has(active.wrapper)) active.controller.abort();
    } else {
      pending = pump();
    }
  };
  const schedule = () => {
    if (!disposed && frame == null) frame = window.requestAnimationFrame(refresh);
  };
  root.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
  observer?.observe(root);
  refresh();

  return {
    refresh: schedule,
    dispose: () => {
      disposed = true;
      if (frame != null) window.cancelAnimationFrame(frame);
      root.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
      active?.controller.abort();
      retained.forEach(releasePdfPageCanvas);
      retained.clear();
      return pending.finally(() => pages.forEach(releasePdfPageCanvas));
    },
  };
};
